import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Award, BookOpen, BrainCircuit, Briefcase, Building2, Calculator, CheckCircle2, Code2, Cpu, GraduationCap, MapPin, Network, Rocket, Search, ShieldCheck, Sparkles, Users, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import CourseCard from "@/components/CourseCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CAT_ICONS = { Code2, Cpu, Sparkles, Briefcase, GraduationCap, BrainCircuit, Calculator, Rocket };
const QUICK_CHIPS = ["AI", "Data Science", "Python", "Cyber Security", "Tally", "Web Development", "B.Voc", "Electronics", "Beauty & Wellness", "Spoken English"];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [query, setQuery] = useState("");
  const [certId, setCertId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/courses/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/pathways").then((r) => setPathways(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(query.trim() ? `/courses?q=${encodeURIComponent(query.trim())}` : "/courses");
  };

  return (
    <div className="bg-navy-base overflow-x-hidden" data-testid="home-page">
      <SEO title="XTM TECHNOLOGIES Jorhat — The FutureSkills Academy | AI, IT & Vocational Courses in Assam"
        description="Industry-aligned courses, professional certifications, WILP B.Voc degree pathways, AI career programs and practical training in Jorhat, Assam. Enlightening Emerging Technologies." />

      {/* HERO */}
      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 rounded-full px-4 py-1.5 text-xs font-semibold text-orange-300 uppercase tracking-[0.15em] mb-6 animate-fade-up" data-testid="hero-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-node" /> Jorhat, Assam — The FutureSkills Academy
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6 animate-fade-up" data-testid="hero-heading">
              Build Your Future with <span className="text-gradient-orange">Future-Ready Skills</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl animate-fade-up" data-testid="hero-subheading">
              Industry-aligned courses, professional certifications, degree pathways, AI career programs and practical training in Jorhat, Assam.
            </p>

            <form onSubmit={doSearch} className="glass rounded-2xl p-2 flex items-center gap-2 max-w-2xl mb-4 animate-fade-up" data-testid="hero-search-form">
              <Search className="w-5 h-5 text-orange-400 ml-3 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What do you want to learn?"
                className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-base py-3"
                data-testid="hero-search-input"
              />
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl px-6 transition-colors" data-testid="hero-search-btn">
                Search
              </Button>
            </form>
            <div className="flex flex-wrap gap-2 mb-10">
              {QUICK_CHIPS.map((chip) => (
                <button key={chip} onClick={() => navigate(`/courses?q=${encodeURIComponent(chip)}`)}
                  className="text-xs font-medium text-slate-300 border border-white/15 hover:border-orange-500/50 hover:text-orange-300 rounded-full px-3 py-1.5 transition-colors"
                  data-testid={`hero-chip-${chip.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                  {chip}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 transition-colors" data-testid="hero-explore-courses-btn">
                <Link to="/courses">EXPLORE COURSES <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 hover:text-orange-300 rounded-full px-8 transition-colors" data-testid="hero-guidance-btn">
                <Link to="/career-ai">GET CAREER GUIDANCE</Link>
              </Button>
            </div>
          </div>

          <div className="mt-14 flex flex-wrap gap-3 text-xs text-slate-400" data-testid="hero-domains">
            {["AI", "Data", "Cloud", "Cybersecurity", "Software", "Electronics", "Business", "Professional Skills"].map((d) => (
              <span key={d} className="flex items-center gap-2 border border-white/10 rounded-full px-3.5 py-2 bg-navy-surface/60">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-node" />{d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="categories-section">
        <SectionHeader eyebrow="Course Categories" title="Seven Tracks. One Future."
          desc="From emerging tech to vocational skills — every program is practical, certified and career-oriented." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden">
          {categories.map((c, i) => {
            const Icon = CAT_ICONS[c.icon] || BookOpen;
            return (
              <Link key={c.slug} to={`/courses/category/${c.slug}`}
                className={`group relative bg-card border border-white/10 rounded-2xl p-7 hover:border-orange-500/50 transition-colors overflow-hidden ${i === 0 ? "lg:col-span-2" : ""}`}
                data-testid={`category-card-${c.slug}`}>
                <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors" />
                <Icon className="w-8 h-8 text-orange-400 mb-4" />
                <h3 className="font-display text-xl font-semibold text-white mb-2 group-hover:text-orange-300 transition-colors">{c.name}</h3>
                <p className="text-sm text-slate-400 mb-4">{c.tagline}</p>
                <span className="text-xs font-semibold text-orange-400">{c.course_count} programs →</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* POPULAR COURSES */}
      <section className="bg-navy-deep/50 py-20" data-testid="popular-courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Popular Programs" title="Featured Courses at XTM"
            desc="Live batches, verifiable certification and project-based learning."
            action={<Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full" data-testid="view-all-courses-btn"><Link to="/courses">View All Courses</Link></Button>} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 8).map((c) => <CourseCard key={c.slug} course={c} />)}
          </div>
        </div>
      </section>

      {/* AI ACCELERATOR BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="ai-accelerator-section">
        <div className="relative rounded-3xl overflow-hidden glow-card bg-card">
          <div className="absolute inset-0 cyber-grid opacity-40" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center p-8 sm:p-12">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Flagship Programs</div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">AI Accelerator Career Programs</h2>
              <p className="text-base text-slate-300 leading-relaxed mb-6">
                Accelerate your career with Artificial Intelligence, Generative AI, Data, Automation and emerging technologies — with projects, tools and career tracks.
              </p>
              <ul className="space-y-2.5 mb-8">
                {["AI Foundations & Machine Learning", "Generative AI & Prompt Engineering", "Data Analytics & Business Intelligence", "AI career tracks with projects & internship pathways"].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300"><CheckCircle2 className="w-4.5 h-4.5 w-5 h-5 text-orange-400 shrink-0" />{t}</li>
                ))}
              </ul>
              <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-7 transition-colors" data-testid="ai-accelerator-cta">
                <Link to="/career-programs">Explore AI Programs <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
            <div className="relative">
              <img src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="AI career programs at XTM Jorhat" className="rounded-2xl w-full h-72 lg:h-80 object-cover animate-float" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* WILP + TALLY STRIPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid lg:grid-cols-2 gap-6" data-testid="wilp-tally-section">
        <StripCard
          icon={<GraduationCap className="w-8 h-8 text-orange-400" />}
          eyebrow="WILP B.Voc Programs"
          title="Learn + Work + Build Experience"
          desc="Work-Integrated Learning Programmes — 3-year B.Voc degree pathways in Application Development and Information Technology with industry experience built in."
          to="/wilp-bvoc" cta="Explore B.Voc Programs" testid="wilp-strip" />
        <StripCard
          icon={<Calculator className="w-8 h-8 text-orange-400" />}
          eyebrow="Tally Education"
          title="TallyPrime, GST & Commerce Skills"
          desc="Practical accounting training on TallyPrime with GST, payroll and inventory — plus preparation support for the Tally Commerce Aptitude Test (TCAT)."
          to="/tally-education" cta="Explore Tally Education" testid="tally-strip" />
      </section>

      {/* CAREER PATHWAYS */}
      <section className="bg-navy-deep/50 py-20" data-testid="pathways-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Career Pathways" title="Visual Roadmaps to Real Careers"
            desc="See exactly how a student in Jorhat progresses from classroom to career."
            action={<Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full" data-testid="view-pathways-btn"><Link to="/career-pathways">All Pathways</Link></Button>} />
          <div className="grid md:grid-cols-3 gap-6">
            {pathways.map((p) => (
              <div key={p.id} className="bg-card border border-white/10 rounded-2xl p-6 hover:border-orange-500/40 transition-colors" data-testid={`pathway-card-${p.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <h3 className="font-display text-lg font-semibold text-white mb-1">{p.title}</h3>
                <p className="text-xs text-orange-400 mb-4">{p.goal}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {p.steps.map((s, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium text-slate-300 bg-navy-deep border border-white/10 rounded-full px-2.5 py-1">{s}</span>
                      {i < p.steps.length - 1 && <span className="text-orange-500 text-xs">→</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY XTM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="why-xtm-section">
        <SectionHeader eyebrow="Why XTM" title="An Academy Built for Careers, Not Just Certificates" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            [Wrench, "Practical Learning", "Theory + Practical + Project in every program, with lab-based training in Jorhat."],
            [BrainCircuit, "Emerging Technologies", "AI, Generative AI, Data, Cloud, Cybersecurity and IoT — skills for the next decade."],
            [Award, "Verifiable Certification", "Every certificate carries a QR-verifiable ID that employers can check instantly."],
            [Users, "Career-Oriented Programs", "Career pathways, internship opportunities and counselling from admissions to completion."],
            [Building2, "Institutional Training", "Programs for schools, colleges, corporates, government organizations and NGOs."],
            [Network, "Work-Integrated Degrees", "WILP B.Voc pathways combining academic degrees with real work experience."],
            [ShieldCheck, "Trusted Credentials", "ISO 9001:2015 Certified, MSME and GST registered organization."],
            [MapPin, "Local, Accessible, WhatsApp-First", "Study in Jorhat, hybrid options, and instant counselling on WhatsApp."],
          ].map(([Icon, title, desc]) => (
            <div key={title} className="bg-card border border-white/10 rounded-2xl p-6 hover:border-orange-500/40 transition-colors" data-testid={`why-${title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <Icon className="w-7 h-7 text-orange-400 mb-4" />
              <h3 className="font-display text-base font-semibold text-white mb-2">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EVENTS */}
      {events.length > 0 && (
        <section className="bg-navy-deep/50 py-20" data-testid="events-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader eyebrow="Workshops & Events" title="Upcoming at the Academy"
              action={<Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full" data-testid="view-events-btn"><Link to="/events">All Events</Link></Button>} />
            <div className="grid md:grid-cols-3 gap-6">
              {events.map((e) => (
                <Link key={e.slug} to="/events" className="bg-card border border-white/10 rounded-2xl p-6 hover:border-orange-500/40 transition-colors block" data-testid={`event-card-${e.slug}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-orange-400 border border-orange-500/40 rounded-full px-2.5 py-1">{e.type}</span>
                  <h3 className="font-display text-lg font-semibold text-white mt-4 mb-2">{e.title}</h3>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">{e.description}</p>
                  <p className="text-xs text-slate-500">{e.date} • {e.venue}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VERIFY CERTIFICATE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="verify-section">
        <div className="relative rounded-3xl overflow-hidden bg-card border border-white/10 p-8 sm:p-12">
          <div className="absolute inset-0 cyber-grid opacity-30" />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Certificate Verification</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">Verify an XTM Certificate Instantly</h2>
            <p className="text-sm text-slate-400 mb-6">Students and employers can verify authenticity using the Certificate ID printed on the certificate (e.g. XTM-CERT-2026-000123).</p>
            <form onSubmit={(e) => { e.preventDefault(); if (certId.trim()) navigate(`/verify/${encodeURIComponent(certId.trim().toUpperCase())}`); }} className="flex gap-2">
              <input value={certId} onChange={(e) => setCertId(e.target.value)} placeholder="Enter Certificate ID"
                className="flex-1 bg-navy-deep border border-white/15 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-orange-500/60 text-sm"
                data-testid="home-verify-input" />
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="home-verify-btn">Verify</Button>
            </form>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy-deep/50 py-20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Student Voices" title="Learning Stories"
            desc="Sample testimonials shown for demonstration — genuine, verified student stories are published via the Admin CMS." />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t) => (
              <div key={t.id} className="bg-card border border-white/10 rounded-2xl p-6" data-testid={`testimonial-${t.id}`}>
                <p className="text-sm text-slate-300 leading-relaxed mb-5">“{t.testimonial}”</p>
                <div className="flex items-center gap-3">
                  <img src={t.photo} alt={t.student_name} className="w-10 h-10 rounded-full object-cover border border-white/15" loading="lazy" />
                  <div>
                    <div className="text-sm font-semibold text-white">{t.student_name} <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 border border-white/15 rounded-full px-1.5 py-0.5 ml-1">Demo</span></div>
                    <div className="text-xs text-slate-500">{t.course} • {t.batch}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faqs" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="faq-section">
        <SectionHeader eyebrow="FAQs" title="Questions, Answered" />
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={f.id} value={`faq-${i}`} className="bg-card border border-white/10 rounded-xl px-5 data-[state=open]:border-orange-500/40">
              <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-white hover:text-orange-300 hover:no-underline" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-400 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* COUNSELLING CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="counselling-cta-section">
        <div className="relative rounded-3xl overflow-hidden glow-card bg-gradient-to-r from-navy-surface to-navy-base p-10 sm:p-16 text-left">
          <div className="absolute inset-0 cyber-grid opacity-40" />
          <div className="relative">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Talk to a Career Counsellor Today</h2>
            <p className="text-base text-slate-300 max-w-xl mb-8">Not sure which course fits your goal? Get free one-on-one guidance — online, on WhatsApp, or at our Jorhat campus.</p>
            <div className="flex flex-wrap gap-4">
              <EnquiryDialog type="counselling" title="Book Career Counselling" trigger={
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 transition-colors" data-testid="counselling-book-btn">
                  Book Free Counselling
                </Button>
              } />
              <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 transition-colors" data-testid="counselling-call-btn">
                <a href="tel:+919101380454">Call +91 9101380454</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, title, desc, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">{eyebrow}</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{title}</h2>
        {desc && <p className="text-base text-slate-400 mt-3">{desc}</p>}
      </div>
      {action}
    </div>
  );
}

function StripCard({ icon, eyebrow, title, desc, to, cta, testid }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-8 hover:border-orange-500/40 transition-colors" data-testid={testid}>
      {icon}
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mt-4 mb-2">{eyebrow}</div>
      <h3 className="font-display text-xl sm:text-2xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-6">{desc}</p>
      <Link to={to} className="inline-flex items-center text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors" data-testid={`${testid}-cta`}>
        {cta} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Link>
    </div>
  );
}
